import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { GrantForm } from "./-components/grant-form";
import { GrantTable } from "./-components/grant-table";
import type { GrantRow } from "./-components/grants";
import { useBuildingGrants } from "./-components/use-building-grants";
import { useGrant } from "./-components/use-grant";

export const Route = createFileRoute("/_authenticated/_knowledge/policies/access")({
  component: FacilityPolicyAccessPage,
});

/** Access to every policy in one building at once, rather than one policy at a time: who may read
 * the whole shelf, and who may change it. */
function FacilityPolicyAccessPage() {
  const { facilityId } = useFacility();
  const canManage = useCan("policy:manage");
  const canSeeFacilities = useCan("facility:read");
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<GrantRow | null>(null);

  const facilities = useQuery({
    ...orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
    enabled: canSeeFacilities,
  });

  const buildings = facilities.data ?? [];
  // Granted one building at a time: the switcher's, or the only one there is.
  const only = buildings.length === 1 ? (buildings[0]?.id ?? "") : "";
  const building = facilityId === "all" ? only : facilityId;

  const { facilityGrants: grants, peopleWithout } = useBuildingGrants(building, canManage);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.knowledge.policies.key() });

  const grant = useGrant();
  const revoke = useMutation(
    orpc.knowledge.policies.revokeFacilityGrant.mutationOptions({
      onSuccess: async () => {
        toast.success("Access removed");
        setRemoving(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const rows = grants.data ?? [];
  const people = peopleWithout(rows);

  return (
    <>
      <PageHeader
        title="Policy access"
        meta="Who may read and change every policy in a building."
        actions={
          <Button asChild variant="outline">
            <Link to="/policies">Back to policies</Link>
          </Button>
        }
      />

      {!canManage ? (
        <EmptyState
          title="Nobody else can be given access from here"
          description="Managing policy access is for the people who look after policies."
        />
      ) : (
        <>
          {building === "" ? (
            <OneBuildingCard prompt="Choose a building. Policy access is granted one building at a time." />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Give access in this building</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <GrantForm
                  idPrefix="facility-grant"
                  people={people}
                  pending={grant.isPending}
                  emptyLabel="Everybody assigned to this building already has access here."
                  onGrant={(who, level) =>
                    building !== "" && grant.mutate({ scope: { facilityId: building }, who, level })
                  }
                />
              </CardContent>
            </Card>
          )}

          {building === "" ? null : grants.isPending ? (
            <Skeleton className="h-64 w-full" />
          ) : grants.isError ? (
            <ReadFailed
              message="Could not load access."
              error={grants.error}
              onRetry={() => void grants.refetch()}
              retrying={grants.isFetching}
            />
          ) : (
            <GrantTable
              rows={rows}
              canManage={canManage}
              emptyTitle="Nobody has building-wide access"
              emptyDescription="Access here covers every policy in this building."
              onRemove={setRemoving}
            />
          )}
        </>
      )}

      <AlertDialog
        open={removing !== null}
        onOpenChange={(open) => (open ? null : setRemoving(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access?</AlertDialogTitle>
            <AlertDialogDescription>
              {removing?.name} loses access to every policy in this building. Access granted on a
              single policy is not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => removing && revoke.mutate({ grantId: removing.id })}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
